<script>
  /**
   * One event as a program row: a date column, the title with its place, and a small picture.
   * Shared by the "Program" story layout and the calendar's archive, so both read the same.
   * `card` turns it into the calendar's program card: the cover large on top, the date line,
   * the title and the place below (the parent lays the cards out, see CalendarProgram).
   *
   * The date column answers "when" at a glance:
   * - a running exhibition shows when it ends ("bis Freitag / 30. Okt. / seit 26. Juli")
   * - a future multi-day event shows its first day and its last ("Sonntag / 26. Juli / bis 30. Okt.")
   * - a single day shows weekday, date and time
   */
  import { page } from '$app/state';
  import t from '#lib/i18n.svelte.js';
  import { LANG } from '#lib/i18n.svelte.js';
  import Image from '#lib/Image.svelte';
  import CategoryLabel from '#lib/CategoryLabel.svelte';
  import { get_entry_category } from '#lib/entry_category.js';
  import { format_date, format_price, generate_css_variables, localize_utc_date, render_micro_markdown } from '#lib/util.js';

  let {
    entry_preview,
    /** @type {string|undefined} link target; the row is a link when set */
    href = undefined,
    /** @type {boolean} */
    show_image = true,
    /** @type {Date} reference point for "running" */
    now = new Date(),
    /** @type {boolean} one line per event: date, category, title (archive lists) */
    compact = false,
    /** @type {boolean} compact rows: same day as the row above, the date stays blank */
    repeat_date = false,
    /** @type {boolean} a card with a large cover (the calendar's "now" and "coming up") */
    card = false,
    class: css_class = '',
  } = $props();

  const category = $derived(get_entry_category(entry_preview.tag_keys, page.data.entry_tag_colors));
  // Admins see which rows the public does not
  const visibility_flag = $derived(
    page.data.current_user?.role === 'admin' && entry_preview.visibility && entry_preview.visibility !== 'public'
      ? t('not_public')
      : ''
  );

  const starts = $derived(entry_preview.starts_at ? new Date(entry_preview.starts_at) : null);
  const ends = $derived(entry_preview.ends_at ? new Date(entry_preview.ends_at) : starts);

  /** Business-local calendar day as YYYY-MM-DD, so midnight in UTC does not shift the date */
  function local_day(date) {
    return localize_utc_date(date).toISOString().slice(0, 10);
  }

  const multi_day = $derived(starts && ends && local_day(starts) !== local_day(ends));
  const running = $derived(multi_day && starts <= now && ends >= now);
  const show_year = $derived(starts && localize_utc_date(starts).getUTCFullYear() !== localize_utc_date(now).getUTCFullYear());

  function day_label(date) {
    return format_date(date, show_year ? 'dMMMY' : 'dMMM');
  }

  function time_range() {
    if (entry_preview.all_day) return t('all_day');
    const start_time = format_date(starts, 'Hm');
    const end_time = ends ? format_date(ends, 'Hm') : start_time;
    return end_time === start_time ? start_time : `${start_time}–${end_time}`;
  }

  const when = $derived.by(() => {
    if (!starts) return null;
    if (running) {
      return {
        weekday: `${t('until')} ${format_date(ends, 'EEEE')}`,
        weekday_short: `${t('until')} ${format_date(ends, 'EEE')}`,
        day: day_label(ends),
        detail: `${t('since')} ${format_date(starts, 'dMMMM')}`,
      };
    }
    if (multi_day) {
      return {
        weekday: format_date(starts, 'EEEE'),
        weekday_short: format_date(starts, 'EEE'),
        day: day_label(starts),
        detail: `${t('until')} ${format_date(ends, 'dMMM')}`,
      };
    }
    return {
      weekday: format_date(starts, 'EEEE'),
      weekday_short: format_date(starts, 'EEE'),
      day: day_label(starts),
      detail: time_range(),
    };
  });

  const price = $derived(
    Number.isInteger(entry_preview.price_amount) && entry_preview.price_amount > 0
      ? format_price(entry_preview.price_amount, entry_preview.price_currency || 'EUR', LANG)
      : ''
  );
  const place_line = $derived([entry_preview.subtitle, price].filter(Boolean).join(' · '));
  const status_label = $derived(
    ['cancelled', 'postponed'].includes(entry_preview.event_status) ? t(`event_status_${entry_preview.event_status}`) : ''
  );
  const image = $derived(show_image && !compact ? entry_preview.images?.[0] : null);

  const compact_date = $derived.by(() => {
    if (!starts) return entry_preview.preamble || '';
    const first = format_date(starts, 'EEEdMMM');
    return multi_day ? `${first} – ${format_date(ends, 'dMMM')}` : first;
  });
</script>

{#if compact}
<svelte:element
  this={href ? 'a' : 'div'}
  {href}
  class='program-row compact {css_class}'
  class:is-cancelled={!!status_label}
>
  <span class='compact-date text-control' class:repeat={repeat_date}>{compact_date}</span>
  <span class='compact-category'>
    {#if category}<CategoryLabel {category} text_style='text-preamble' />{/if}
  </span>
  <span class='compact-title text-body'>
    {@html render_micro_markdown(entry_preview.title || t('untitled'), [], 'text-body')}
    {#if status_label}<span class='status text-preamble'> · {status_label}</span>{/if}
    {#if visibility_flag}<span class='flag text-preamble'> · {visibility_flag}</span>{/if}
  </span>
</svelte:element>
{:else if card}
<svelte:element
  this={href ? 'a' : 'div'}
  {href}
  class='program-card {css_class}'
  class:is-cancelled={!!status_label}
>
  <!-- Flyers carry the event: full column width in their own proportions, never cropped. The
       covers of a row share their lower edge, so the dates and titles below line up. An event
       without a picture gets a poster in its category's colour that fills the same space -->
  {#if image}
    <div class='cover'>
      <Image class='cover-image' {...image} sizes='(min-width: 641px) 50vw, 100vw' loading='lazy' />
    </div>
  {:else}
    <div class='cover poster' class:has-category={!!category} style={category ? generate_css_variables(category.color_id) : ''} aria-hidden='true'>
      <span class='poster-title text-heading2'>{@html render_micro_markdown(entry_preview.title || t('untitled'), [], 'text-heading2')}</span>
    </div>
  {/if}
  <div class='card-text'>
    <p class='card-meta'>
      {#if when}
        <span class='card-when'>
          <span class='card-day text-control'>{when.weekday_short} {when.day}</span>
          <span class='detail text-caption'>· {when.detail}</span>
        </span>
      {:else if entry_preview.preamble}
        <span class='card-when text-preamble'>{entry_preview.preamble}</span>
      {/if}
      {#if category || status_label || visibility_flag}
        <span class='eyebrow'>
          {#if category}<CategoryLabel {category} text_style='text-preamble' />{/if}
          {#if status_label}<span class='status text-preamble'>{status_label}</span>{/if}
          {#if visibility_flag}<span class='flag text-preamble'>{visibility_flag}</span>{/if}
        </span>
      {/if}
    </p>
    <h3 class='title text-heading3'>{@html render_micro_markdown(entry_preview.title || t('untitled'), [], 'text-heading3')}</h3>
    {#if place_line}
      <p class='place text-caption'>{place_line}</p>
    {/if}
  </div>
</svelte:element>
{:else}

<svelte:element
  this={href ? 'a' : 'div'}
  {href}
  class='program-row {css_class}'
  class:has-image={!!image}
  class:is-cancelled={!!status_label}
>
  <p class='when'>
    {#if when}
      <!-- Narrow date columns take the short weekday: "Donnerstag" would break mid-word -->
      <span class='weekday text-preamble'><span class='long'>{when.weekday}</span><span class='short'>{when.weekday_short}</span></span>
      <span class='day text-heading3'>{when.day}</span>
      <span class='detail text-caption'>{when.detail}</span>
    {:else if entry_preview.preamble}
      <span class='weekday text-preamble'>{entry_preview.preamble}</span>
    {/if}
  </p>
  <div class='what'>
    {#if category || status_label || visibility_flag}
      <p class='eyebrow'>
        {#if category}<CategoryLabel {category} text_style='text-preamble' />{/if}
        {#if status_label}<span class='status text-preamble'>{status_label}</span>{/if}
        {#if visibility_flag}<span class='flag text-preamble'>{visibility_flag}</span>{/if}
      </p>
    {/if}
    <h3 class='title text-heading3'>{@html render_micro_markdown(entry_preview.title || t('untitled'), [], 'text-heading3')}</h3>
    {#if place_line}
      <p class='place text-caption'>{place_line}</p>
    {/if}
  </div>
  {#if image}
    <!-- The picture keeps its own proportions (flyers come in every shape): never cropped, never
         boxed, its top on the row's first line -->
    <div class='thumb-box'>
      <Image class='thumb' {...image} sizes='(min-width: 769px) 240px, 120px' loading='lazy' />
    </div>
  {/if}
</svelte:element>
{/if}

<style lang='postcss'>
  .program-row {
    display: grid;
    grid-template-columns: minmax(7rem, 2fr) minmax(0, 7fr) minmax(4rem, 1.5fr);
    column-gap: var(--grid-gutter);
    align-items: start;
    padding-block: var(--size-5);
    border-block-end: 1px solid var(--color-default-stroke);
    color: inherit;
    text-decoration: none;
    @media (max-width: 640px) {
      grid-template-columns: 4.75rem minmax(0, 1fr) 5.5rem;
      column-gap: var(--size-4);
    }
  }
  .when {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    margin: 0;
    font-variant-numeric: tabular-nums;
    .weekday, .detail {
      color: var(--text-2);
    }
    .short {
      display: none;
    }
    @media (max-width: 640px) {
      .long {
        display: none;
      }
      .short {
        display: inline;
      }
    }
  }
  /* Long titles stop after a few lines; the event page has the full one */
  .title, .compact-title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    overflow: hidden;
  }
  .compact-title {
    -webkit-line-clamp: 3;
  }
  .what {
    display: flex;
    flex-direction: column;
    gap: var(--size-3);
    min-width: 0;
    .title {
      margin: 0;
      text-wrap: balance;
    }
    .place {
      margin: 0;
      color: var(--text-2);
    }
    .status {
      color: var(--text-1);
    }
  }
  .eyebrow {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--size-1) var(--size-5);
    margin: 0;
  }
  .flag {
    color: var(--text-2);
  }
  .is-cancelled :is(.title, .compact-title) {
    text-decoration: line-through;
    text-decoration-thickness: 1px;
  }
  /* Archive: date, category, title on one line; the title wraps under itself */
  .program-row.compact {
    grid-template-columns: minmax(7rem, 2fr) minmax(0, 2fr) minmax(0, 7fr);
    align-items: baseline;
    padding-block: var(--size-4);
    .compact-date {
      color: var(--text-2);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      &.repeat {
        visibility: hidden;
      }
    }
    .compact-title {
      min-width: 0;
      max-width: none;
    }
    @media (max-width: 640px) {
      grid-template-columns: 6.5rem minmax(0, 1fr);
      row-gap: var(--size-2);
      .compact-date {
        grid-row: 1 / span 2;
        white-space: normal;
      }
      .compact-category {
        grid-column: 2;
        grid-row: 1;
      }
      .compact-category:empty {
        display: none;
      }
      .compact-title {
        grid-column: 2;
      }
    }
  }
  .thumb-box {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    min-width: 0;
    :global(picture.thumb) {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }
    :global(img.thumb) {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 15rem;
      border-radius: var(--radius-xs, 0px);
      @media (max-width: 640px) {
        max-height: 7rem;
      }
    }
  }
  /* Card: two rows of the parent grid (cover, text), so neighbouring cards align */
  .program-card {
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 2;
    row-gap: var(--size-5);
    min-width: 0;
    color: inherit;
    text-decoration: none;
  }
  .cover {
    align-self: end;
    display: flex;
    min-width: 0;
    container-type: inline-size;
    :global(picture.cover-image) {
      display: flex;
      width: 100%;
    }
    /* A portrait flyer stops a little below square, so it does not tower over its neighbour */
    :global(img.cover-image) {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 85cqi;
      border-radius: var(--radius-xs, 0px);
    }
  }
  /* No picture: as tall as the neighbouring cover, at least a landscape flyer */
  .cover.poster {
    align-self: stretch;
    align-items: flex-end;
    /* the spacer holds the landscape minimum; the row's other cover may stretch it taller */
    &::before {
      content: '';
      padding-block-start: calc(100% / 1.414);
    }
    border-radius: var(--radius-xs, 0px);
    /* without a category a quiet tint of the page, with one its colour */
    background: color-mix(in oklch, var(--color-foreground) 7%, var(--color-background));
    color: var(--color-foreground);
    &.has-category {
      background: var(--color-emphasis-background);
      color: var(--color-emphasis-foreground);
    }
    .poster-title {
      margin: var(--size-6);
      max-width: 22ch;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 5;
      overflow: hidden;
      text-wrap: balance;
      color: inherit;
    }
  }
  .card-text {
    display: flex;
    flex-direction: column;
    gap: var(--size-3);
    min-width: 0;
    .title {
      margin: 0;
      text-wrap: balance;
    }
    .place {
      margin: 0;
      color: var(--text-2);
    }
    .status {
      color: var(--text-1);
    }
  }
  /* The category follows the date, so it never sits next to the neighbouring card */
  .card-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--size-2) var(--size-7);
    margin: 0;
    font-variant-numeric: tabular-nums;
    .detail {
      color: var(--text-2);
    }
  }
  a:is(.program-row, .program-card) {
    @media (hover: hover) {
      &:hover :is(.title, .compact-title) {
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 0.15em;
      }
    }
  }
</style>
